import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ClientDashboard from "./pages/ClientDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import ApplicationTracker from "./pages/ApplicationTracker";
import TaskManager from "./pages/TaskManager";
import FactFinder from "./pages/FactFinder";
import AIAnalysis from "./pages/AIAnalysis";
import InvoiceManager from "./pages/InvoiceManager";
import EmailTemplates from "./pages/EmailTemplates";
import AppointmentBooking from "./pages/AppointmentBooking";
import ComplaintsPolicy from "./pages/ComplaintsPolicy";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsConditions from "./pages/TermsConditions";
import CookiePolicy from "./pages/CookiePolicy";
import LinkInBio from "./pages/LinkInBio";
import FirstTimeBuyerGuide from "./pages/FirstTimeBuyerGuide";
import RemortgageGuide from "./pages/RemortgageGuide";
import IslamicFinanceGuide from "./pages/IslamicFinanceGuide";
import DocumentsRequired from "./pages/DocumentsRequired";
import BestRateTable from "./pages/BestRateTable";
import ProtectionGuide from "./pages/ProtectionGuide";
import AdvisorsPage from "./pages/AdvisorsPage";
import { AuthProvider, useAuth } from "./context/AuthContext";

const ProtectedRoute = ({ children, staffOnly = false, adminOnly = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  // Staff only routes (admin or advisor)
  if (staffOnly && !["admin", "advisor"].includes(user.role)) {
    return <Navigate to="/dashboard" />;
  }
  
  // Admin only routes
  if (adminOnly && user.role !== "admin") {
    return <Navigate to="/dashboard" />;
  }
  
  return children;
};

function AppRoutes() {
  const { user } = useAuth();
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/book-appointment" element={<AppointmentBooking />} />
      
      {/* Policy Pages */}
      <Route path="/complaints-policy" element={<ComplaintsPolicy />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-conditions" element={<TermsConditions />} />
      <Route path="/cookie-policy" element={<CookiePolicy />} />
      
      {/* Link in Bio Pages */}
      <Route path="/advisors" element={<AdvisorsPage />} />
      <Route path="/bio/zahirul" element={<LinkInBio brokerId="zahirul" />} />
      <Route path="/bio/mihir" element={<LinkInBio brokerId="mihir" />} />
      <Route path="/bio/gill" element={<LinkInBio brokerId="gill" />} />
      <Route path="/bio/sarah" element={<LinkInBio brokerId="broker2" />} />
      <Route path="/first-time-buyer-guide" element={<FirstTimeBuyerGuide />} />
      <Route path="/remortgage-guide" element={<RemortgageGuide />} />
      <Route path="/islamic-finance-guide" element={<IslamicFinanceGuide />} />
      <Route path="/documents-required" element={<DocumentsRequired />} />
      <Route path="/best-rates" element={<BestRateTable />} />
      <Route path="/protection-guide" element={<ProtectionGuide />} />
      
      {/* Client Dashboard */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <ClientDashboard />
          </ProtectedRoute>
        }
      />
      
      {/* Staff Only Routes (Admin + Advisor) */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute staffOnly>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tasks"
        element={
          <ProtectedRoute staffOnly>
            <TaskManager />
          </ProtectedRoute>
        }
      />
      
      {/* Routes accessible by all authenticated users */}
      <Route
        path="/applications"
        element={
          <ProtectedRoute>
            <ApplicationTracker />
          </ProtectedRoute>
        }
      />
      <Route
        path="/fact-finder"
        element={
          <ProtectedRoute>
            <FactFinder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-analysis"
        element={
          <ProtectedRoute>
            <AIAnalysis />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices"
        element={
          <ProtectedRoute>
            <InvoiceManager />
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-templates"
        element={
          <ProtectedRoute staffOnly>
            <EmailTemplates />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
